import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { seriesClipLabel } from '@/lib/dicom/seriesLabel';
import { seriesImageCount } from '@/lib/viewer/seriesShape';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteSeriesThumbnail } from './DeleteSeriesThumbnail';
import { SeriesPreviewPanel } from './SeriesPreviewPanel';
/**
 * Grid of series thumbnails for selecting series to delete.
 * Features a split-view with hover preview panel on the left.
 */
export function DeleteSeriesGrid({ series, selectedSeriesIds, onSelectionChange, }) {
    const { t } = useTranslation('viewer');
    const [hoveredSeriesId, setHoveredSeriesId] = useState(null);
    const totalSeries = series.length;
    const selectedCount = selectedSeriesIds.size;
    // Get the currently hovered series data
    const hoveredSeries = hoveredSeriesId ? series.find((s) => s.id === hoveredSeriesId) : null;
    // Calculate total images in selected series. Series-level delete needs no
    // instance rows, so this reads the series' summary count (seriesShape.ts) —
    // never `images.length`, which is silently 0 on a series the user hasn't
    // paged in yet.
    const selectedImageCount = series
        .filter((s) => selectedSeriesIds.has(s.id))
        .reduce((acc, s) => acc + seriesImageCount(s), 0);
    const handleToggleSelect = useCallback((seriesId) => {
        const newSelection = new Set(selectedSeriesIds);
        if (newSelection.has(seriesId)) {
            newSelection.delete(seriesId);
        }
        else {
            newSelection.add(seriesId);
        }
        onSelectionChange(newSelection);
    }, [selectedSeriesIds, onSelectionChange]);
    const handleSelectAll = useCallback(() => {
        const allIds = new Set(series.map((s) => s.id));
        onSelectionChange(allIds);
    }, [series, onSelectionChange]);
    const handleDeselectAll = useCallback(() => {
        onSelectionChange(new Set());
    }, [onSelectionChange]);
    if (totalSeries === 0) {
        return (_jsx("div", { className: "text-muted-foreground flex h-48 items-center justify-center", children: t('delete.noSeriesFound') }));
    }
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2 px-1", children: [_jsxs("div", { className: "text-muted-foreground text-sm", children: [_jsx("span", { className: "text-foreground font-medium", children: selectedCount }), ' ', t('delete.seriesSelectedOf', { total: totalSeries }), selectedCount > 0 && (_jsxs("span", { className: "ml-2", children: ["(", t('delete.imageCount', { count: selectedImageCount }), ")"] }))] }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { variant: "outline", size: "sm", onClick: handleSelectAll, disabled: selectedCount === totalSeries, children: t('delete.selectAll') }), _jsx(Button, { variant: "outline", size: "sm", onClick: handleDeselectAll, disabled: selectedCount === 0, children: t('delete.deselectAll') })] })] }), _jsxs("div", { className: "flex gap-4", children: [_jsx(SeriesPreviewPanel, { series: hoveredSeries, isVisible: !!hoveredSeries }), _jsx("div", { className: "max-h-[400px] min-w-0 flex-1 overflow-y-auto pr-2", onMouseLeave: () => setHoveredSeriesId(null), children: _jsx("div", { className: "grid grid-cols-3 gap-4 p-2 sm:grid-cols-4 md:grid-cols-5", children: series.map((s) => {
                                // Get thumbnail from first image or series thumbnails
                                const thumbnailSrc = s.thumbnails?.[0]?.storageLocation || s.images?.[0]?.storageLocation || null;
                                return (_jsx(DeleteSeriesThumbnail, { seriesUid: s.id, src: thumbnailSrc, seriesNumber: s.seriesNumber, clipLabel: seriesClipLabel(s, t), modality: s.modality, imageCount: seriesImageCount(s), isSelected: selectedSeriesIds.has(s.id), isHovered: hoveredSeriesId === s.id, onToggleSelect: handleToggleSelect, onHover: setHoveredSeriesId }, s.id));
                            }) }) })] })] }));
}
